import { format } from 'date-fns';
import { Box, Stack, Typography } from '@mui/material';
import { CustomerGuest } from '../../types/customers';
import { BORDER, HAIRLINE, SURFACE_HOVER } from './profile/colors';

interface Props {
  guest: CustomerGuest;
  onEdit?: (guest: CustomerGuest) => void;
}

export default function GuestProfileRow({ guest, onEdit }: Props) {
  const guestName = `${guest.firstName} ${guest.lastName}`.trim();
  const initials = [guest.firstName?.[0], guest.lastName?.[0]]
    .filter(Boolean)
    .join('')
    .toUpperCase();
  const guestDobStr = guest.dob
    ? new Date(guest.dob).toISOString().split('T')[0]
    : null;
  const guestDob = guestDobStr
    ? format(new Date(`${guestDobStr}T00:00:00`), 'MM/dd/yyyy')
    : null;
  const subline = [guestDob, guest.email].filter(Boolean).join(' · ');

  return (
    <Stack
      direction="row"
      alignItems="center"
      gap="12px"
      onClick={onEdit ? () => onEdit(guest) : undefined}
      sx={{
        width: '100%',
        boxSizing: 'border-box',
        padding: '12px',
        border: `1px solid ${HAIRLINE}`,
        borderRadius: '10px',
        backgroundColor: '#fff',
        cursor: onEdit ? 'pointer' : 'default',
      }}>
      <Stack
        alignItems="center"
        justifyContent="center"
        sx={{
          width: 36,
          height: 36,
          borderRadius: '50%',
          backgroundColor: SURFACE_HOVER,
          color: 'text.secondary',
          flexShrink: 0,
          typography: 'caption',
          fontWeight: 600,
        }}>
        {initials || '?'}
      </Stack>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="h5" sx={{ color: 'text.primary' }}>
          {guestName}
        </Typography>
        {subline && (
          <Typography
            variant="caption"
            component="p"
            sx={{
              color: 'text.tertiary',
              marginTop: '3px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
            {subline}
          </Typography>
        )}
        {guest.note && (
          <Typography
            variant="caption"
            component="p"
            sx={{
              color: 'text.tertiary',
              marginTop: '3px',
              fontStyle: 'italic',
            }}>
            {guest.note}
          </Typography>
        )}
      </Box>
      {onEdit && (
        <Box
          component="button"
          type="button"
          onClick={e => {
            e.stopPropagation();
            onEdit(guest);
          }}
          sx={{
            flexShrink: 0,
            fontFamily: 'inherit',
            color: 'text.primary',
            background: 'none',
            border: `1px solid ${BORDER}`,
            borderRadius: '5px',
            padding: '6px 12px',
            cursor: 'pointer',
            '&:hover': { borderColor: 'common.black' },
            typography: 'h5',
          }}>
          Edit
        </Box>
      )}
    </Stack>
  );
}
